import React, { useState, useEffect } from 'react';
import './ContactPreview.css';
import { X, Save, User, Mail, Phone, Building2, MapPin, Globe, AlertCircle } from 'lucide-react';

interface ContactData {
  displayName?: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
  companyName?: string;
  department?: string;
  emails?: Array<{ email: string; type: string; is_primary?: boolean; isPrimary?: boolean }>;
  phones?: Array<{ number: string; type: string; is_primary?: boolean; isPrimary?: boolean }>;
  addresses?: Array<{ street?: string; city?: string; state?: string; postalCode?: string; postal_code?: string; country?: string; type?: string }>;
  website?: string;
  socialProfiles?: Array<{ platform: string; url: string }>;
  notes?: string;
  category?: string;
  tags?: string | string[];
}

interface ContactPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  contactData: ContactData | null;
  onSave: (data: ContactData, category?: string, tags?: string[]) => void;
  isLoading?: boolean;
  ocrMethod?: string;
  confidence?: number;
}

export const ContactPreview: React.FC<ContactPreviewProps> = ({
  isOpen,
  onClose,
  contactData,
  onSave,
  isLoading = false,
  ocrMethod,
  confidence
}) => {
  const [editedData, setEditedData] = useState<ContactData>({});
  const [category, setCategory] = useState('Other');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (contactData) {
      setEditedData(contactData);
      
      // Initialize category and tags from existing contact data if available
      if (contactData.category) {
        setCategory(contactData.category);
      } else {
        setCategory('Other');
      }
      
      if (contactData.tags) {
        // tags could be a string (comma-separated) or array
        if (typeof contactData.tags === 'string') {
          setTags(contactData.tags.split(',').map(t => t.trim()).filter(t => t));
        } else if (Array.isArray(contactData.tags)) {
          setTags(contactData.tags);
        }
      } else {
        setTags([]);
      }
    }
  }, [contactData]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store original values
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;
      
      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // Apply body scroll lock
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      // Cleanup: restore original values when modal closes
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [isOpen]);

  const handleSave = () => {
    onSave(editedData, category, tags);
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      const newTags = [...tags, tagInput.trim()];
      setTags(newTags);
      setTagInput('');
    }
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const removeTag = (tag: string) => {
    const newTags = tags.filter(t => t !== tag);
    setTags(newTags);
  };

  const updateField = (field: keyof ContactData, value: any) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const updateEmail = (index: number, value: string) => {
    const emails = [...(editedData.emails || [])];
    emails[index] = { ...emails[index], email: value };
    updateField('emails', emails);
  };

  const addEmail = () => {
    const emails = [...(editedData.emails || []), { email: '', type: 'WORK', isPrimary: false }];
    updateField('emails', emails);
  };

  const removeEmail = (index: number) => {
    const emails = (editedData.emails || []).filter((_, i) => i !== index);
    updateField('emails', emails);
  };

  const updatePhone = (index: number, value: string) => {
    const phones = [...(editedData.phones || [])];
    phones[index] = { ...phones[index], number: value };
    updateField('phones', phones);
  };

  const updatePhoneType = (index: number, type: string) => {
    const phones = [...(editedData.phones || [])];
    phones[index] = { ...phones[index], type };
    updateField('phones', phones);
  };

  const addPhone = () => {
    const phones = [...(editedData.phones || []), { number: '', type: 'WORK', isPrimary: false }];
    updateField('phones', phones);
  };

  const removePhone = (index: number) => {
    const phones = (editedData.phones || []).filter((_, i) => i !== index);
    updateField('phones', phones);
  };

  const updateAddress = (index: number, field: string, value: string) => {
    const addresses = [...(editedData.addresses || [])];
    addresses[index] = { ...addresses[index], [field]: value };
    updateField('addresses', addresses);
  };

  const addAddress = () => {
    const addresses = [...(editedData.addresses || []), { street: '', city: '', state: '', postalCode: '', country: '', type: 'WORK' }];
    updateField('addresses', addresses);
  };

  const removeAddress = (index: number) => {
    const addresses = (editedData.addresses || []).filter((_, i) => i !== index);
    updateField('addresses', addresses);
  };

  if (!isOpen || !contactData) return null;

  return (
    <div className="contact-preview-overlay" onClick={onClose}>
      <div className="contact-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="contact-preview-header">
          <div className="preview-title">
            <User size={24} />
            <div>
              <h2>{ocrMethod ? 'Review Contact Information' : 'Edit Contact Information'}</h2>
              {ocrMethod && (
                <span className="ocr-badge">
                  Extracted with {ocrMethod === 'form_recognizer' ? 'Form Recognizer' : ocrMethod === 'openai_gpt4_vision' ? 'GPT-4 Vision' : 'Computer Vision'}
                  {confidence && <span className="confidence"> • {Math.round(confidence * 100)}% confidence</span>}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="btn-close-preview">
            <X size={24} />
          </button>
        </div>

        <div className="contact-preview-content">
          {ocrMethod && (
            <div className="preview-notice">
              <AlertCircle size={18} />
              <span>Please review and correct any errors before saving</span>
            </div>
          )}

          {/* Basic Information */}
          <div className="preview-section">
            <h3><User size={20} /> Basic Information</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>Display Name *</label>
                <input
                  type="text"
                  value={editedData.displayName || ''}
                  onChange={(e) => updateField('displayName', e.target.value)}
                  placeholder="John Doe"
                />
              </div>
              <div className="form-field">
                <label>First Name</label>
                <input
                  type="text"
                  value={editedData.givenName || ''}
                  onChange={(e) => updateField('givenName', e.target.value)}
                  placeholder="John"
                />
              </div>
              <div className="form-field">
                <label>Last Name</label>
                <input
                  type="text"
                  value={editedData.surname || ''}
                  onChange={(e) => updateField('surname', e.target.value)}
                  placeholder="Doe"
                />
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="preview-section">
            <h3><Building2 size={20} /> Company Information</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>Job Title</label>
                <input
                  type="text"
                  value={editedData.jobTitle || ''}
                  onChange={(e) => updateField('jobTitle', e.target.value)}
                  placeholder="Software Engineer"
                />
              </div>
              <div className="form-field">
                <label>Company</label>
                <input
                  type="text"
                  value={editedData.companyName || ''}
                  onChange={(e) => updateField('companyName', e.target.value)}
                  placeholder="Acme Inc."
                />
              </div>
              <div className="form-field">
                <label>Department</label>
                <input
                  type="text"
                  value={editedData.department || ''}
                  onChange={(e) => updateField('department', e.target.value)}
                  placeholder="Engineering"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="preview-section">
            <h3><Mail size={20} /> Contact Information</h3>
            
            {/* Emails */}
            <div className="form-field">
              <label>Emails</label>
              {editedData.emails && editedData.emails.map((email, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Mail size={16} style={{ marginTop: '0.75rem', color: '#6b7280' }} />
                  <input
                    type="email"
                    value={email.email}
                    onChange={(e) => updateEmail(index, e.target.value)}
                    placeholder="email@example.com"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => removeEmail(index)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addEmail}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  marginTop: '0.5rem'
                }}
              >
                + Add Email
              </button>
            </div>

            {/* Phones */}
            <div className="form-field">
              <label>Phones</label>
              {editedData.phones && editedData.phones.map((phone, index) => (
                <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem', padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  {/* Row 1: Icon + Input */}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <Phone size={16} style={{ color: '#6b7280', flexShrink: 0 }} />
                    <input
                      type="tel"
                      value={phone.number}
                      onChange={(e) => updatePhone(index, e.target.value)}
                      placeholder="+1 234 567 8900"
                      style={{ flex: 1 }}
                    />
                  </div>
                  
                  {/* Row 2: Chip Selection + Remove Button */}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.375rem', flex: 1, flexWrap: 'wrap' }}>
                      {['WORK', 'CELL', 'HOME', 'FAX'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => updatePhoneType(index, type)}
                          style={{
                            padding: '0.375rem 0.75rem',
                            fontSize: '0.813rem',
                            border: phone.type === type ? 'none' : '1px solid #d1d5db',
                            background: phone.type === type ? '#3b82f6' : 'white',
                            color: phone.type === type ? 'white' : '#6b7280',
                            borderRadius: '9999px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            fontWeight: phone.type === type ? '500' : '400',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            if (phone.type !== type) {
                              e.currentTarget.style.background = '#f3f4f6';
                              e.currentTarget.style.borderColor = '#9ca3af';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (phone.type !== type) {
                              e.currentTarget.style.background = 'white';
                              e.currentTarget.style.borderColor = '#d1d5db';
                            }
                          }}
                        >
                          {type === 'WORK' ? 'Work' : type === 'CELL' ? 'Mobile' : type === 'HOME' ? 'Home' : 'Fax'}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => removePhone(index)}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addPhone}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  marginTop: '0.5rem'
                }}
              >
                + Add Phone
              </button>
            </div>

            {/* Website */}
            <div className="form-field">
              <label>Website</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Globe size={16} style={{ marginTop: '0.75rem', color: '#6b7280' }} />
                <input
                  type="url"
                  value={editedData.website || ''}
                  onChange={(e) => updateField('website', e.target.value)}
                  placeholder="https://example.com"
                  style={{ flex: 1 }}
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="preview-section">
            <h3><MapPin size={20} /> Address</h3>
            {editedData.addresses && editedData.addresses.map((addr, index) => (
              <div key={index} style={{ marginBottom: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                <div className="form-field">
                  <label>Street</label>
                  <input
                    type="text"
                    value={addr.street || ''}
                    onChange={(e) => updateAddress(index, 'street', e.target.value)}
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>City</label>
                    <input
                      type="text"
                      value={addr.city || ''}
                      onChange={(e) => updateAddress(index, 'city', e.target.value)}
                      placeholder="New York"
                    />
                  </div>
                  <div className="form-field">
                    <label>State</label>
                    <input
                      type="text"
                      value={addr.state || ''}
                      onChange={(e) => updateAddress(index, 'state', e.target.value)}
                      placeholder="NY"
                    />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Postal Code</label>
                    <input
                      type="text"
                      value={addr.postalCode || addr.postal_code || ''}
                      onChange={(e) => updateAddress(index, 'postalCode', e.target.value)}
                      placeholder="10001"
                    />
                  </div>
                  <div className="form-field">
                    <label>Country</label>
                    <input
                      type="text"
                      value={addr.country || ''}
                      onChange={(e) => updateAddress(index, 'country', e.target.value)}
                      placeholder="USA"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeAddress(index)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    marginTop: '0.5rem'
                  }}
                >
                  Remove Address
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addAddress}
              style={{
                padding: '0.5rem 1rem',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              + Add Address
            </button>
          </div>

          {/* Category and Tags */}
          <div className="preview-section">
            <h3>Organization</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="Professional">Professional</option>
                  <option value="Personal">Personal</option>
                  <option value="Client">Client</option>
                  <option value="Prospect">Prospect</option>
                  <option value="Partner">Partner</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <div className="form-field">
              <label>
                Tags {tags.length > 0 && <span style={{ color: '#0058d8', fontWeight: 'bold' }}>({tags.length} added)</span>}
              </label>
              <div className="tags-input">
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder="Type a tag and press Enter"
                    style={{ flex: 1 }}
                  />
                  <button 
                    type="button"
                    onClick={addTag}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#0058d8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Add Tag
                  </button>
                </div>
                {tags.length > 0 && (
                  <div className="tags-list">
                    {tags.map((tag, index) => (
                      <span key={index} className="tag">
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {tags.length === 0 && (
                <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                  No tags added yet. Type a tag above and click "Add Tag" or press Enter.
                </small>
              )}
            </div>
          </div>
        </div>

        <div className="contact-preview-footer">
          <button onClick={onClose} className="btn-cancel" disabled={isLoading}>
            Cancel
          </button>
          <button onClick={handleSave} className="btn-save" disabled={isLoading || !editedData.displayName}>
            {isLoading ? (
              <>
                <div className="spinner"></div>
                <span>{ocrMethod ? 'Saving...' : 'Updating...'}</span>
              </>
            ) : (
              <>
                <Save size={20} />
                <span>{ocrMethod ? 'Save Contact' : 'Update Contact'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactPreview;
